import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Collapse,
  useTheme,
  useMediaQuery,
  Container,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  Security,
  Code,
  LocalShipping,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';

interface LayoutProps {
  children: React.ReactNode;
}

interface SubmenuItem {
  label: string;
  path: string;
  icon?: React.ReactElement;
}

interface NavigationItem {
  label: string;
  path: string;
  submenu?: SubmenuItem[];
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navigationItems: NavigationItem[] = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'Services',
      path: '/services',
      submenu: [
        {
          label: 'Custom Software Development',
          path: '/services/custom-software',
          icon: <Code />,
        },
        {
          label: 'Managed IT Services',
          path: '/services/managed-it',
          icon: <Security />,
        },
        {
          label: 'Logistics Consulting',
          path: '/services/logistics',
          icon: <LocalShipping />,
        },
      ],
    },
    {
      label: 'About',
      path: '/about',
    },
    {
      label: 'Knowledge',
      path: '/knowledge',
      submenu: [
        {
          label: 'Blog',
          path: '/knowledge/blog',
        },
        {
          label: 'Case Studies',
          path: '/knowledge/case-studies',
        },
      ],
    },
    {
      label: 'Contact',
      path: '/contact',
    },
  ];

  const drawer = (
    <Box sx={{ width: 250 }}>
      <Toolbar />
      <List>
        {navigationItems.map((item) => (
          <React.Fragment key={item.label}>
            <ListItemButton
              onClick={() => {
                if (item.submenu) {
                  if (item.label === 'Services') {
                    setServicesOpen(!servicesOpen);
                  } else if (item.label === 'Knowledge') {
                    setResourcesOpen(!resourcesOpen);
                  }
                } else {
                  navigate(item.path);
                  setMobileOpen(false);
                }
              }}
            >
              <ListItemText primary={item.label} />
              {item.submenu && (
                <>
                  {item.label === 'Services' && (servicesOpen ? <ExpandLess /> : <ExpandMore />)}
                  {item.label === 'Knowledge' && (resourcesOpen ? <ExpandLess /> : <ExpandMore />)}
                </>
              )}
            </ListItemButton>
            {item.submenu && (
              <Collapse in={item.label === 'Services' ? servicesOpen : resourcesOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.submenu.map((subItem) => (
                    <ListItemButton
                      key={subItem.label}
                      sx={{ pl: 4 }}
                      onClick={() => {
                        navigate(subItem.path);
                        setMobileOpen(false);
                      }}
                    >
                      {subItem.icon && <Box sx={{ mr: 1 }}>{subItem.icon}</Box>}
                      <ListItemText
                        primary={subItem.label}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" color="primary" elevation={1}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Typography
              variant="h6"
              component="div"
              sx={{ 
                flexGrow: 0, 
                mr: 4,
                fontWeight: 700,
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              INTELLUSIA STUDIOS
            </Typography>
            
            {!isMobile && (
              <Box sx={{ flexGrow: 1, display: 'flex', gap: 2, ml: 4 }}>
                {navigationItems.map((item) => (
                  <Button
                    key={item.label}
                    color="inherit"
                    onClick={() => {
                      if (!item.submenu) {
                        navigate(item.path);
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (item.submenu) {
                        if (item.label === 'Services') setServicesOpen(true);
                        if (item.label === 'Knowledge') setResourcesOpen(true);
                      }
                    }}
                    sx={{
                      fontWeight: 500,
                      position: item.submenu ? 'relative' : 'static',
                    }}
                  >
                    {item.label}
                    {item.submenu && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          bgcolor: 'background.paper',
                          boxShadow: 3,
                          borderRadius: 1,
                          pt: 1,
                          minWidth: 280,
                          display: item.label === 'Services' ? (servicesOpen ? 'block' : 'none') :
                                   item.label === 'Knowledge' ? (resourcesOpen ? 'block' : 'none') : 'none',
                        }}
                        onMouseEnter={() => {
                          if (item.label === 'Services') setServicesOpen(true);
                          if (item.label === 'Knowledge') setResourcesOpen(true);
                        }}
                        onMouseLeave={() => {
                          if (item.label === 'Services') setServicesOpen(false);
                          if (item.label === 'Knowledge') setResourcesOpen(false);
                        }}
                      >
                        {item.submenu.map((subItem) => (
                          <Box
                            key={subItem.label}
                            sx={{
                              px: 2,
                              py: 1.5, 
                              color: 'text.primary',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              '&:hover': {
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                              },
                            }}
                            onClick={() => navigate(subItem.path)}
                          >
                            {subItem.icon && <Box sx={{ mr: 1.5 }}>{subItem.icon}</Box>}
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {subItem.label}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Button>
                ))}
              </Box>
            )}

            <Box sx={{ flexGrow: 1 }} />
            
            <Button
              variant="contained"
              color="secondary"
              sx={{ 
                ml: 2,
                display: { xs: 'none', md: 'block' },
              }}
              onClick={() => navigate('/contact')}
            >
              Get Started
            </Button>

            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
        }}
      >
        {drawer}
      </Drawer>

      <Toolbar />
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>
      <Footer />
    </Box>
  );
};

export default Layout;